import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiEdit3, FiFolder } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { isSuperAdminRole } from '../utils/roles';
import BrandLogo from './BrandLogo';
import NotificationBell from './NotificationBell';

type MenuIcon = React.FC<{ className?: string }>;
const FolderIcon = FiFolder as unknown as MenuIcon;
const PostIcon = FiEdit3 as unknown as MenuIcon;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const [imageError, setImageError] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const addRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  const initials = user?.name
    ? user.name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()
    : '??';

  // Handle avatar URL - prepend API_BASE if it's a relative path
  const avatarUrl = user?.avatarUrl 
    ? (user.avatarUrl.startsWith('http') ? user.avatarUrl : `${API_BASE}${user.avatarUrl}`)
    : null;

  const showAvatar = avatarUrl && !imageError;

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
      if (addRef.current && !addRef.current.contains(event.target as Node)) {
        setAddOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  // Mobile drawer: the sidebar is toggled via a body class so it needs no shared state with pages.
  useEffect(() => {
    document.body.classList.toggle('nav-open', navOpen);
    return () => document.body.classList.remove('nav-open');
  }, [navOpen]);

  useEffect(() => {
    setNavOpen(false);
    setAddOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setNavOpen(false);
        setAddOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const onLogout = async () => {
    setMenuOpen(false);
    await logout();
  };

  return (
    <header className="header">
      <div className="header-left">
        <button
          className="nav-toggle"
          type="button"
          aria-label={navOpen ? 'Close navigation' : 'Open navigation'}
          aria-expanded={navOpen}
          onClick={() => setNavOpen(prev => !prev)}
        >
          <span className="nav-toggle-bar" />
        </button>
        <Link to="/dashboard" className="header-brand" aria-label="VimAPI home">
          <BrandLogo height={26} />
        </Link>
      </div>
      <div className="sidebar-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />
      <div className="header-right">
        <div className="header-actions">
          <Link to="/posts/compose" className="header-action-btn">+ Post</Link>
          <Link to="/projects" className="header-action-btn header-action-primary">+ Project</Link>
        </div>
        <div className="header-add" ref={addRef}>
          <button
            className="header-add-trigger"
            type="button"
            aria-label="Create new"
            aria-expanded={addOpen}
            aria-haspopup="menu"
            onClick={() => setAddOpen(prev => !prev)}
          >
            +
          </button>
          {addOpen && (
            <div className="user-menu-dropdown header-add-menu" role="menu">
              <Link to="/projects" className="user-menu-item" role="menuitem" onClick={() => setAddOpen(false)}>
                <FolderIcon className="header-add-icon" />
                Project
              </Link>
              <Link to="/posts/compose" className="user-menu-item" role="menuitem" onClick={() => setAddOpen(false)}>
                <PostIcon className="header-add-icon" />
                Post
              </Link>
            </div>
          )}
        </div>
        <div className="search-wrapper">
          <span className="search-shortcut">⌘K</span>
          <input className="search-bar" placeholder="Search..." />
        </div>
        <NotificationBell />
        <div className="user-info" ref={menuRef}>
          <button
            className="user-menu-trigger"
            type="button"
            onClick={() => setMenuOpen(prev => !prev)}
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            {showAvatar ? (
              <img
                src={avatarUrl}
                alt={user?.name || 'User'}
                className="user-avatar-img"
                onError={() => setImageError(true)}
              />
            ) : (
              <span className="user-avatar">{initials}</span>
            )}
            <span className="user-name">{user?.name || 'User'} ▾</span>
          </button>

          {menuOpen && (
            <div className="user-menu-dropdown" role="menu">
              <Link
                to="/profile"
                className="user-menu-item"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
              >
                Profile
              </Link>
              <Link
                to="/account/billing"
                className="user-menu-item"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
              >
                Subscription
              </Link>
              {isSuperAdminRole(user?.role) && (
                <Link
                  to="/admin/users"
                  className="user-menu-item"
                  role="menuitem"
                  onClick={() => setMenuOpen(false)}
                >
                  Admin Console
                </Link>
              )}
              <Link
                to="/account"
                className="user-menu-item"
                role="menuitem"
                onClick={() => setMenuOpen(false)}
              >
                Settings
              </Link>
              <button className="user-menu-item user-menu-signout" onClick={onLogout}>
                Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
